'use client';
import React, { useMemo } from 'react';
import type { Dividend } from '@benzinga/calendar-manager';
import { formatPercentage } from '@benzinga/utils/numberUtils';
import type { ColumnDef } from '@benzinga/table';
import { DIVIDEND_STATUS_LABELS, getDividendStatus, type DividendStatus } from '../../utils/quote/dividendStatus';
import { formatDate } from './formatters';
import QuoteListTable from './QuoteListTable';

export const formatDividendAmount = (value?: string): string => {
  if (value == null || value === '' || value === '0') return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '—';
  return `$${amount < 1 ? amount.toFixed(4) : amount.toFixed(2)}`;
};

export const formatYield = (value?: string): string => {
  if (value == null || value === '' || value === '0') return '—';
  const yieldDecimal = Number(value);
  if (Number.isNaN(yieldDecimal)) return '—';
  return `${formatPercentage(yieldDecimal * 100)}%`;
};

const DIVIDEND_STATUS_FLAG_CLASS: Record<DividendStatus, string> = {
  declared: 'bg-blue-100 text-blue-700',
  estimated: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-700',
};

const DividendStatusFlag: React.FC<{ dividend: Dividend }> = ({ dividend }) => {
  const status = getDividendStatus(dividend);
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${DIVIDEND_STATUS_FLAG_CLASS[status]}`}>
      {DIVIDEND_STATUS_LABELS[status]}
    </span>
  );
};

export const dividendsColumnsDef: ColumnDef<Dividend>[] = [
  {
    cellRenderer: ({ data }) => <DividendStatusFlag dividend={data} />,
    field: 'confirmed',
    headerClass: 'text-left',
    headerName: 'Status',
    valueGetter: ({ data }) => DIVIDEND_STATUS_LABELS[getDividendStatus(data)],
  },
  {
    cellClass: 'text-gray-700',
    field: 'ex_dividend_date',
    headerClass: 'text-left',
    headerName: 'Ex-Date',
    valueFormatter: ({ data }) => formatDate(data.ex_dividend_date),
  },
  {
    cellClass: 'text-right font-medium text-gray-900',
    field: 'dividend',
    headerClass: 'text-right',
    headerName: 'Dividend',
    valueFormatter: ({ data }) => formatDividendAmount(data.dividend),
  },
  {
    cellClass: 'text-right text-gray-700',
    field: 'dividend_yield',
    headerClass: 'text-right',
    headerName: 'Yield',
    valueFormatter: ({ data }) => formatYield(data.dividend_yield),
  },
  {
    cellClass: 'text-gray-700',
    field: 'date',
    headerClass: 'text-left',
    headerName: 'Announced',
    valueFormatter: ({ data }) => formatDate(data.date),
  },
  {
    cellClass: 'text-gray-700',
    field: 'record_date',
    headerClass: 'text-left',
    headerName: 'Record',
    valueFormatter: ({ data }) => formatDate(data.record_date),
  },
  {
    cellClass: 'text-gray-700',
    field: 'payable_date',
    headerClass: 'text-left',
    headerName: 'Payable',
    valueFormatter: ({ data }) => formatDate(data.payable_date),
  },
];

export const DividendsHistoryTable: React.FC<{ dividends: Dividend[] }> = ({ dividends }) => {
  const rows = useMemo(
    () =>
      [...dividends].sort((a, b) => {
        const aKey = a.ex_dividend_date || a.date || '';
        const bKey = b.ex_dividend_date || b.date || '';
        return bKey.localeCompare(aKey);
      }),
    [dividends],
  );

  return (
    <QuoteListTable
      columnsDef={dividendsColumnsDef}
      gateType="dividends"
      noDataText="No dividend history available."
      rowData={rows}
    />
  );
};

export default DividendsHistoryTable;
